import React, { useEffect, ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, Card, Image, Button, Stack } from 'react-bootstrap';
import { map, isUndefined, isNull } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { BackButton, ClaimBadge, Loading, LoadingError, NoData } from '@components/Base';
import { PaymentMethods } from '@models/PaymentInfo';
import { getHealthClaimType } from '@models/HealthClaim';
import { ClaimableTypes } from '@models/Claim';
import { amountToUsd } from '@utils/currency';
import { formatDateTime } from '@utils/datetime';
import { getAttachmentType, isAttachmentImage, isAttachmentPdf } from '@utils/attachment';

import { selectHealthClaim } from './selector';
import { healthClaimFetch } from './action';
import { routeUserDashboardClaimEdit } from '../../route';
import { useEditable } from '../hook';

interface RowItemProps {
  label: string;
  value?: string | number | ReactElement | null;
  list?: string[];
  renderItem?: (item: string) => void;
}

const RowItem = ({ label, value, list, renderItem }: RowItemProps) => {
  return (
    <Row className="mb-2">
      <Col xs={4} sm={4} md={3}>
        {label}
      </Col>
      <Col xs="auto">:</Col>
      <Col>
        <>
          {value && value}
          {list && renderItem && map(list, renderItem)}
        </>
      </Col>
    </Row>
  );
};

interface ClaimAttachmentItemProps {
  name: string;
  url: string;
}

const ClaimAttachmentItem = ({ name, url }: ClaimAttachmentItemProps) => {
  const { t } = useTranslation();
  const attachmentType = getAttachmentType(url);

  return (
    <>
      {isAttachmentImage(attachmentType) && (
        <Image src={url} thumbnail className="mb-3" data-testid={`${name}-image`} />
      )}

      {isAttachmentPdf(attachmentType) && (
        <a href={url} className="d-block mb-3" target="_blank" rel="noreferrer" data-testid={`${name}-pdf`}>
          {t('common:clickToViewDetails')}
        </a>
      )}
    </>
  );
};

const HealthClaimDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { claimableType, claimableId } = useParams();
  const { healthClaim } = useSelector(selectHealthClaim);
  const isLoaded = !isUndefined(healthClaim);
  const isError = isNull(healthClaim);
  const hasHealthClaim = isLoaded && !isNull(healthClaim);
  const shouldEdit = useEditable(healthClaim);
  const isPaymentByBankTransfer = healthClaim?.paymentInfo?.paymentMethod === PaymentMethods.BankTransfer;

  useEffect(() => {
    if (claimableId) {
      dispatch(healthClaimFetch({ id: Number(claimableId) }));
    }
  }, [claimableId]);

  return (
    <>
      <BackButton className="mb-4" />
      {!isLoaded && <Loading />}
      {isError && <LoadingError />}
      {isLoaded && !hasHealthClaim && <NoData />}

      {healthClaim && (
        <Card>
          <Card.Header>
            <Stack direction="horizontal" gap={2} className="align-items-center">
              <h5 className="mb-0 me-auto">{t('common:details')}</h5>
              {shouldEdit && (
                <Button
                  as={Link}
                  to={routeUserDashboardClaimEdit({
                    claimableType: claimableType as ClaimableTypes,
                    claimableId: Number(claimableId),
                  })}
                  size="sm"
                >
                  {t('common:edit')}
                </Button>
              )}
            </Stack>
          </Card.Header>
          <Card.Body>
            <RowItem label={t('common:status')} value={<ClaimBadge status={healthClaim.status} />} />
            <RowItem label={t('common:claimNumber')} value={healthClaim.claimNumber} />
            <RowItem
              label={t('healthInsuranceClaimForm.accidentHappenedAt.label')}
              value={formatDateTime(healthClaim.accidentHappenedAt)}
            />
            <RowItem label={t('claimForm.claimType.label')} value={getHealthClaimType(healthClaim.claimType)} />
            <RowItem
              label={t('claimDetails.supportingDocuments')}
              list={healthClaim.supportingDocuments}
              renderItem={(url) => <ClaimAttachmentItem name="supporting-documents" url={url} key={url} />}
            />
            <RowItem
              label={t('common:additionalDocuments')}
              list={healthClaim.additionalDocuments}
              renderItem={(url) => <ClaimAttachmentItem key={url} name="additional-documents" url={url} />}
            />
            <RowItem
              label={t('claimForm.totalClaimedAmount.label')}
              value={amountToUsd(healthClaim.totalClaimedAmount)}
            />
            <RowItem label={t('claimForm.approvedAmount.label')} value={amountToUsd(healthClaim.approvedAmount)} />
            <RowItem label={t('common:note')} value={healthClaim.note} />
            <hr />
            <RowItem label={t('paymentInfo.paymentMethod')} value={healthClaim.paymentInfo?.paymentMethod} />
            <RowItem label={t('paymentInfo.beneficiaryName')} value={healthClaim.paymentInfo?.beneficiaryName} />
            {isPaymentByBankTransfer && (
              <>
                <RowItem label={t('paymentInfo.bankName')} value={healthClaim.paymentInfo?.bankName} />
                <RowItem label={t('paymentInfo.bankAccount')} value={healthClaim.paymentInfo?.bankAccount} />
              </>
            )}
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export { HealthClaimDetails };
